import Nachbarn from './Nachbarn'
import type { Einreichung } from '../lib/warteschlangen'

/**
 * Links, was am Ort steht; rechts, was der Nutzer behauptet; Unterschiede
 * hervorgehoben (§8.4).
 *
 * Angewandt wird genau, was in `werte` steht — nichts wird aus `gesehen`
 * abgeleitet. `gesehen` steht trotzdem darunter: Es ist der Nachweis, dass
 * eine Frage gestellt wurde, und mehr behauptet es nicht.
 *
 * Bei `place_edit` kann ein **Ortsname** dabei sein. Er steht oben, vor den
 * Merkmalen, weil er das Auffälligste ist — und bei einer reinen Umbenennung
 * die einzige Zeile.
 */

const HERKUNFT_TEXT: Record<string, string> = {
  osm: 'aus OpenStreetMap übernommen — dort meist richtig',
  editorial: 'redaktionell eingetragen',
  user: 'von einem Nutzer eingetragen',
}

/**
 * `initial_source` beschreibt die Herkunft des **Ortes**. Hat er einen Namen,
 * stammt der von dort — hat er keinen, wäre „der heutige Name: aus
 * OpenStreetMap" eine Aussage über etwas, das es nicht gibt.
 */
function herkunftSatz(herkunft: string, hatNamen: boolean): string {
  const text = HERKUNFT_TEXT[herkunft] ?? herkunft
  return hatNamen ? `Der heutige Name: ${text}.` : `Der Ort: ${text} — bisher ohne Namen.`
}

interface Zeile {
  schluessel: string
  alt: unknown
  neu: unknown
  spalte: string
}

function darstellen(wert: unknown): string {
  if (wert === undefined) return '—'
  if (wert === null) return 'leer'
  if (typeof wert === 'boolean') return wert ? 'ja' : 'nein'
  return String(wert)
}

export default function Gegenueberstellung({ e }: { e: Einreichung }) {
  const zeilen: Zeile[] = []

  // Der Name **nur** bei `place_edit` — eine `info`-Einreichung ergänzt
  // Ausstattung und benennt nichts um. Stünde dort je einer, zeigt ihn die
  // Liste der nicht übernommenen Schlüssel.
  const neuerName = e.kind === 'place_edit' ? e.vorschau.name : null
  const nameAnders = neuerName !== null && neuerName !== e.ort_name

  for (const [k, neu] of Object.entries(e.vorschau.features)) {
    zeilen.push({
      schluessel: k,
      neu,
      alt: e.ort_features?.[k],
      spalte: 'features',
    })
  }
  for (const [k, neu] of Object.entries(e.vorschau.accessibility)) {
    zeilen.push({
      schluessel: k,
      neu,
      alt: e.ort_accessibility?.[k],
      spalte: 'accessibility',
    })
  }
  if (e.vorschau.ageMinMonths !== null) {
    zeilen.push({
      schluessel: 'Alter ab (Monate)',
      neu: e.vorschau.ageMinMonths,
      alt: e.ort_age_min_months,
      spalte: 'age_min_months',
    })
  }
  if (e.vorschau.ageMaxMonths !== null) {
    zeilen.push({
      schluessel: 'Alter bis (Monate)',
      neu: e.vorschau.ageMaxMonths,
      alt: e.ort_age_max_months,
      spalte: 'age_max_months',
    })
  }

  const unbekannt = Object.entries(e.vorschau.unbekannt)

  return (
    <div className="karte">
      <h3>Was sich ändern würde</h3>

      {neuerName !== null && (
        <div className="namenszeile">
          <table className="tabelle">
            <thead>
              <tr>
                <th>Name</th>
                <th>am Ort</th>
                <th>eingereicht</th>
                <th>Spalte</th>
              </tr>
            </thead>
            <tbody>
              <tr className={nameAnders ? 'anders' : ''}>
                <td>Ortsname</td>
                {/* „—" und nicht „Spielplatz": 12.474 Orte haben keinen Namen.
                    Die Kategorie als Rückfall ließe es aussehen, als würde
                    jemand „Spielplatz" ändern — dabei ist die Auskunft gerade,
                    dass es keinen Namen gab. */}
                <td className="gedaempft">{e.ort_name ?? '—'}</td>
                <td className={nameAnders ? 'wert-neu' : ''}>{neuerName}</td>
                <td className="leise zahl">name</td>
              </tr>
            </tbody>
          </table>
          {e.ort_herkunft && (
            <p className="leise gesehen-zeile">
              {herkunftSatz(e.ort_herkunft, e.ort_name !== null)}
            </p>
          )}
          {!nameAnders && (
            <p className="leise">
              Der eingereichte Name ist der, der schon dasteht — annehmen
              ändert nichts.
            </p>
          )}
        </div>
      )}

      {e.vorschau.hinweis && (
        <div className="karte-innen">
          <p className="leise">Hinweis an die Moderation</p>
          <p className="bewertung-text">{e.vorschau.hinweis}</p>
        </div>
      )}

      {zeilen.length === 0 ? (
        <p className="gedaempft">
          {neuerName !== null
            ? 'Außer dem Namen würde sich nichts ändern — keine Ausstattung, keine Altersangabe.'
            : 'Diese Einreichung würde keine Spalte verändern.'}
        </p>
      ) : (
        <div className="tabelle-huelle">
          <table className="tabelle">
            <thead>
              <tr>
                <th>Merkmal</th>
                <th>am Ort</th>
                <th>eingereicht</th>
                <th>Spalte</th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z) => {
                const anders = darstellen(z.alt) !== darstellen(z.neu)
                return (
                  <tr key={`${z.spalte}:${z.schluessel}`} className={anders ? 'anders' : ''}>
                    <td>{z.schluessel}</td>
                    <td className="gedaempft">{darstellen(z.alt)}</td>
                    <td className={anders ? 'wert-neu' : ''}>{darstellen(z.neu)}</td>
                    <td className="leise zahl">{z.spalte}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="leise gesehen-zeile">
        Gefragt wurde nach:{' '}
        {e.vorschau.gesehen.length > 0 ? e.vorschau.gesehen.join(', ') : '(keine Angabe)'}.
        Das ist der Nachweis, dass die Frage gestellt wurde —{' '}
        <strong>nicht</strong>, dass fehlende Merkmale „gibt es hier nicht"
        bedeuten. Die App fragt danach eigens, und nur dann steht ein
        ausdrückliches „nein" oben in der Tabelle.
      </p>

      {/* Wer einen „Spielplatz" in „Spielplatz Rosenweg" umbenennt, während
          80 m weiter schon einer so heißt, meint vielleicht den anderen. Das
          ist der eine Fall, den nur ein Mensch sieht. */}
      {neuerName !== null &&
        Number.isFinite(e.ort_lat) &&
        Number.isFinite(e.ort_lon) && (
          <Nachbarn
            lat={e.ort_lat as number}
            lon={e.ort_lon as number}
            ausser={e.place_id}
            ueberschrift="Wie die Nachbarn im Umkreis von 150 m heißen"
            leerText="Kein weiterer Ort im Umkreis von 150 m."
          />
        )}

      {unbekannt.length > 0 && (
        <div className="meldung meldung-warnung">
          <p>
            Diese Schlüssel passen zu keiner Abbildungsregel und werden{' '}
            <strong>nicht</strong> übernommen:
          </p>
          <ul className="unbekannt-liste zahl">
            {unbekannt.map(([k, v]) => (
              <li key={k}>
                {k}: {darstellen(v)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
